<template>
    <div class="test-results">
        <h2 class="mt-0">Resultaat voor Persoonlijkheidstest</h2>
        <p class="description">
            De test die je zojuist hebt gedaan, meet 5 persoonlijkheidseigenschappen. Hieronder kan je zien hoe je op
            deze vijf kenmerken hebt gescoord.
        </p>

        <div class="bar-chart mx-auto my-3">
            <design2021-apprentice-basic-bar-chart-component
                :options="chartOptions"
                :series="series">
            </design2021-apprentice-basic-bar-chart-component>
        </div>

        <h2>Emotionele stabiliteit</h2>
        <p>
            Het balkje laat zien hoe emotioneel stabiel jij volgens de test bent. Hoe meer jouw score naar rechts ligt,
            hoe hoger je scoort op emotionele stabiliteit.
        </p>

        <div
            class="mx-auto my-4"
            style="max-width: 650px">
            <design2021-apprentice-range-indicator-component
                :value="resultData['emotional_stability']"
                :max="8"
                :min="0"
                :color="colors['emotional_stability']"
                start-label="Bezorgd"
                end-label="Stabiel">
            </design2021-apprentice-range-indicator-component>
        </div>

        <ul>
            <li>
                Naarmate je meer naar rechts zit met je score, ben je emotioneel stabieler. Dat houdt in dat je meestal
                kalm bent, sterk in je schoenen staat en tegen een stootje kan. Er moet al heel wat gebeuren wil jij
                gestrest of emotioneel raken.
            </li>
            <li>
                Naarmate je meer naar links zit met je score, ben je sneller bezorgd en alerter. Je bent gevoelig van
                aard, kunt emotionele ups en downs ervaren, en kunt last hebben van onzekerheid en stress.
            </li>
        </ul>

        <h3>Wat past er bij je in werk en beroep?</h3>

        <ul>
            <li>
                Emotioneel stabiele mensen kunnen veel verschillende soorten werk doen. Een hoge score op emotionele
                stabiliteit is vooral handig in sectoren en beroepen die risico’s en stress met zich mee brengen en waar
                je een grote verantwoordelijkheid hebt. Voorbeelden van dit soort sectoren zijn defensie, directie,
                luchtvaart en de zorg. Denk aan beroepen zoals beroepsmilitair, ambulanceverpleegkundige, brandweerman,
                luchtvaartpiloot, cardioloog en politicus.
            </li>
            <li>
                Mensen die wat lager scoren op emotionele stabiliteit houden van werk waarin ze hun eigen tempo kunnen
                volgen, waar niet te veel druk op hen wordt uitgeoefend en waar het van belang is alert en attent te
                zijn. Maar ook voor creatieve beroepen kan het nuttig zijn om gevoeliger van aard te zijn. Voorbeelden
                van sectoren waarin je sensitief en alert dient te zijn, zijn dienstverlenend, creatief en/of
                administratief van aard. Denk aan beroepen zoals tekstschrijver, applicatieontwikkelaar, webdesigner,
                boekhouder en hovenier.
            </li>
        </ul>

        <h3>Tips!</h3>
        <ul>
            <li>
                Heb je een hoge score op emotionele stabiliteit? Erger je je dan niet te snel aan mensen die snel
                gestrest zijn. Niet iedereen staat zo sterk in zijn schoenen als jij!
            </li>
            <li>
                Heb je een lage score op emotionele stabiliteit? Beter met stress omgaan kan je gelukkig heel goed
                leren. Ook kunnen mensen over het algemeen beter met stress omgaan naarmate ze ouder worden.
            </li>
        </ul>

        <h2>Extraversie</h2>
        <p>
            Het balkje laat zien hoe extravert jij volgens de test bent. Hoe meer jouw score naar rechts ligt, hoe hoger
            je scoort op extraversie.
        </p>

        <div
            class="mx-auto my-4"
            style="max-width: 650px">
            <design2021-apprentice-range-indicator-component
                :value="resultData['extroversion']"
                :max="8"
                :min="0"
                :color="colors['extroversion']"
                start-label="Introvert"
                end-label="Extravert">
            </design2021-apprentice-range-indicator-component>
        </div>

        <ul>
            <li>
                Naarmate je meer naar rechts zit met je score, ben je meer extravert. Dat wil zeggen, houd je ervan om
                onder de mensen te zijn, praat je graag, ben je meestal vrolijk en spontaan en heb je behoefte aan
                avontuur.
            </li>
            <li>
                Naarmate je meer naar links zit met je score, ben je meer introvert. Dat wil zeggen, denk je graag na,
                kan je je in je eentje goed vermaken en houd je je, in gezelschap, wat meer op de achtergrond.
            </li>
        </ul>

        <h3>Wat pas er bij je in werk en beroep?</h3>
        <ul>
            <li>
                Extraverte mensen houden van werk waarbij ze met mensen moeten omgaan en waarin uitdagingen liggen. Werk
                moet niet te saai zijn. Voorbeelden van sectoren waar extraverte mensen goed passen zijn management,
                marketing, sales, en het onderwijs. Denk aan beroepen zoals accountmanager, medewerker
                kinderdagverblijf, acteur, docent Engels en sportinstructeur.
            </li>
            <li>
                Introverte mensen houden van werk waarbij ze heel zelfstandig kunnen opereren, lekker hun eigen gang
                kunnen gaan en dingen moeten uitzoeken. Voorbeelden van sectoren die introverte mensen aanspreken zijn
                de accountancy, wetenschap en technologie. Denk aan beroepen zoals programmeur, schrijver, laborant,
                schoonmaker en accountant.
            </li>
        </ul>

        <h3>Tips!</h3>
        <ul>
            <li>
                Heb je een hoge score op extraversie? Praten is belangrijk voor een goed contact met mensen, en daarmee
                ook voor de samenwerking. Kijk echter uit dat je je tijd niet te veel verbabbelt.
            </li>
            <li>
                Heb je een lage score op extraversie? Vermijd het contact met de mensen op je werk dan niet onnodig. Ook
                al ben je heel zelfstandig, soms heb je nu eenmaal anderen nodig (of anderen jou!). En moet je een keer
                een presentatie geven? Geen nood, presenteren is een vaardigheid die je kan leren.
            </li>
        </ul>

        <h2>Ordelijkheid</h2>
        <p>
            Het balkje laat zien hoe ordelijk jij volgens de test bent. Hoe meer jouw score naar rechts ligt, hoe hoger
            je scoort op ordelijkheid.
        </p>

        <div
            class="mx-auto my-4"
            style="max-width: 650px">
            <design2021-apprentice-range-indicator-component
                :value="resultData['orderliness']"
                :max="8"
                :min="0"
                :color="colors['orderliness']"
                start-label="Spontaan"
                end-label="Ordelijk">
            </design2021-apprentice-range-indicator-component>
        </div>

        <ul>
            <li>
                Naarmate je meer naar rechts zit met je score, ben je ordelijker en meer gefocust. Dat houdt in dat je
                je aan afspraken houdt, systematisch te werk gaat en houdt van orde en structuur. Je kan hard werken,
                krijgt dingen voor elkaar en hebt oog voor detail.
            </li>
            <li>
                Naarmate je meer naar links zit met je score, ben je spontaner en minder gefocust. Je leeft met de dag
                en bent flexibel in hoe je je werk aanpakt. Je kunt rommel maken en chaotisch worden.
            </li>
        </ul>

        <h3>Wat past er bij je in werk en beroep?</h3>

        <ul>
            <li>
                Ordelijke mensen kunnen veel verschillende soorten werk doen. Een hoge score op ordelijkheid is vooral
                handig in sectoren en beroepen waarin nauwkeurigheid belangrijk is en fouten vervelende gevolgen hebben.
                Bijvoorbeeld in de bouw, IT, voedselbranche, onderzoek en administratieve sector. Denk aan beroepen
                zoals consultant, marktonderzoeker, elektricien, loodgieter, kok, software developer en bouwmanager.
            </li>
            <li>
                Mensen die lager scoren op ordelijkheid houden van variatie in hun werk en kunnen goed improviseren. Dat
                kan handig zijn in functies waarbij flexibiliteit heel belangrijk is. Denk aan beroepen zoals een
                cameraman, horecamedewerker, verkoopadviseur, accountmanager of een tolk.
            </li>
        </ul>

        <h3>Tips!</h3>
        <ul>
            <li>
                Heb je een hoge score op ordelijkheid? Kijk uit dat je niet te perfectionistisch wordt, dat zorgt alleen
                maar voor stress. Meestal is ‘goed’ echt goed genoeg en is perfect niet nodig.
            </li>
            <li>
                Heb je een lage score op ordelijkheid? Kijk dan toch eens wat een goede planning je kan brengen. Je zult
                merken dat je met wat meer orde en structuur minder dingen vergeet en minder fouten maakt.
            </li>
        </ul>

        <h2>Vriendelijkheid</h2>
        <p>
            Het balkje laat zien hoe vriendelijk jij volgens de test bent. Hoe meer jouw score naar rechts ligt, hoe
            hoger je scoort op vriendelijkheid.
        </p>

        <div
            class="mx-auto my-4"
            style="max-width: 650px">
            <design2021-apprentice-range-indicator-component
                :value="resultData['friendliness']"
                :max="8"
                :min="0"
                :color="colors['friendliness']"
                start-label="Competitief"
                end-label="Vriendelijk">
            </design2021-apprentice-range-indicator-component>
        </div>

        <ul>
            <li>
                Naarmate je meer naar rechts zit met je score, ben je vriendelijker en meegaander. Dat wil zeggen dat je
                graag anderen helpt, rekening houdt met andermans gevoelens en harmonie in je relaties met anderen
                belangrijk vindt.
            </li>
            <li>
                Naarmate je meer naar links zit met je score, ben je competitiever en minder vriendelijk. Misschien
                onbedoeld, denk je wat meer aan je eigenbelang en houd je niet altijd even veel rekening met andere
                mensen. Ook kan je direct, ongeduldig of koppig zijn.
            </li>
        </ul>

        <h3>Wat past er bij je in werk en beroep?</h3>

        <ul>
            <li>
                Vriendelijke mensen houden van werk waarbij ze iets kunnen betekenen voor andere mensen. Voorbeelden van
                sectoren die vriendelijke mensen aanspreken zijn de gezondheidszorg en het onderwijs. Denk aan beroepen
                zoals verpleegkundige, leraar basisonderwijs, dierenverzorger, mediator en huisarts.
            </li>
            <li>
                Mensen die lager scoren op vriendelijkheid houden van werk waarbij ze hun eigen doelen en ambities
                kunnen waarmaken. Ze kunnen goed impopulaire maatregelen nemen en werken onder competitieve
                omstandigheden. Voorbeelden van sectoren waarin deze mensen goed functioneren zijn sales, bestuur en
                directie en ordehandhaving. Denk aan beroepen zoals advocaat, beveiliger, manager, straatverkoper of
                vertegenwoordiger.
            </li>
        </ul>

        <h3>Tips!</h3>
        <ul>
            <li>
                Heb je een hoge score op vriendelijkheid? Kijk dan uit dat je niet over je grenzen gaat alleen maar om
                anderen te helpen. Durf op tijd ’nee’ te zeggen. Je kan tenslotte alleen iets voor anderen betekenen als
                je eerst goed voor jezelf zorgt.
            </li>
            <li>
                Heb je een lage score op vriendelijkheid? Realiseer je dan dat je veel kan hebben aan andere mensen,
                mits je ze goed behandelt. Doe jij iets voor anderen, dan doen zij iets voor jou op het moment dat je
                dat nodig hebt.
            </li>
        </ul>

        <h2>Openheid</h2>
        <p>
            Het balkje laat zien hoe open jij, volgens deze test, staat voor nieuwe ervaringen. Hoe meer jouw score naar
            rechts ligt, hoe hoger je scoort op openheid voor ervaringen.
        </p>

        <div
            class="mx-auto my-4"
            style="max-width: 650px">
            <design2021-apprentice-range-indicator-component
                :value="resultData['openness']"
                :max="8"
                :min="0"
                :color="colors['openness']"
                start-label="Traditioneel"
                end-label="Open">
            </design2021-apprentice-range-indicator-component>
        </div>

        <ul>
            <li>
                Naarmate je meer naar rechts zit met je score, sta je meer open voor nieuwe ervaringen. Dat houdt in dat
                je nieuwsgierig en open-minded bent, bereid bent om nieuwe dingen te proberen en veel fantasie en ideeën
                hebt.
            </li>
            <li>
                Naarmate je meer naar links zit met je score, ben je meer traditioneel, sta je minder open voor nieuwe
                ervaringen en experimenten. Je doet de dingen het liefste zoals je ze altijd doet, houdt niet zo van
                veranderingen en bent nuchter van aard.
            </li>
        </ul>

        <h3>Wat past er bij je in werk en beroep?</h3>

        <ul>
            <li>
                Mensen die open staan voor ervaringen doen graag werk waarin ze nieuwe dingen meemaken, over dingen
                kunnen filosoferen of inventief kunnen zijn. Hun creativiteit maakt dat ze in veel sectoren en functies
                kunnen werken. Denk aan een entrepreneur die een nieuw idee heeft en een bedrijf start en aan beroepen
                zoals journalist of copywriter. Andere voorbeelden van beroepen waarin het helpt als je hoog op deze
                eigenschap scoort zijn bijvoorbeeld illustrator, loopbaanadviseur en regisseur.
            </li>
            <li>
                Mensen die lager scoren op openheid voor ervaringen houden van voorspelbaarheid in het werk en zijn wat
                traditioneler. Dat is handig in sectoren waarin het van belang is dat je volgens bestaande regels en
                procedures werkt. Zoals de luchtvaartsector, administratieve sector en bij toezicht en regelgeving. Denk
                aan beroepen zoals controller, toezichthouder, luchtverkeersleider, jurist, machinist, operationeel
                manager en operator.
            </li>
        </ul>

        <h3>Tips!</h3>
        <ul>
            <li>
                Heb je een hoge score op openheid voor ervaringen? Maak je ideeën dan concreet: wie, wat, hoe en wanneer
                moet er iets gebeuren? Anders blijf je hangen in luchtkastelen.
            </li>
            <li>
                Heb je een lage score op openheid voor ervaringen? Wees dan niet te bang om te veranderen. Realiseer je
                dat alles, op den duur, went.
            </li>
        </ul>

        <h2>Tot slot</h2>
        <p>
            Bedenk je altijd dat een hoge of lage score niet goed of slecht is. Voor sommige situaties of functies is
            een hoge score gewenst, terwijl bij andere functies een lagere score beter past. Zoek of creëer dan ook
            altijd een (werk)omgeving waarin je persoonlijkheid het best tot zijn of haar recht komt. Je kunt dan zoveel
            mogelijk jezelf zijn en werkzaamheden doen die passen bij wie je bent. Dat is wel zo prettig.
        </p>
    </div>
</template>

<script>
import Design2021ApprenticeBasicBarChartComponent from '../../../common/charts/design-2021-apprentice-basic-bar-chart-component.vue';
import { getAttributesForKeys, getKeysWithAttribute, sortResults } from '../../../../helpers/testCenter';
import Design2021ApprenticeRangeIndicatorComponent from '../../../common/design-2021-apprentice-range-indicator-component.vue';

export default {
    components: { Design2021ApprenticeRangeIndicatorComponent, Design2021ApprenticeBasicBarChartComponent },
    props: {
        resultData: {
            type: Object,
        },
    },
    data() {
        const testKey = 'job-personality-personality-test-2024-05-16';
        return {
            colors: getKeysWithAttribute(testKey, 'color'),
            series: [
                {
                    data: sortResults(testKey, this.resultData),
                },
            ],
            chartOptions: {
                chart: {
                    toolbar: {
                        tools: {
                            download: false,
                        },
                    },
                },
                plotOptions: {
                    bar: {
                        barHeight: '100%',
                        distributed: true,
                        columnWidth: '50%',
                        dataLabels: {
                            show: false,
                        },
                    },
                },
                colors: getAttributesForKeys(testKey, 'color'),
                xaxis: {
                    categories: getAttributesForKeys(testKey, 'label'),
                    labels: {
                        fontSize: '14px',
                        fontFamily: 'DM Sans',
                    },
                    axisBorder: {
                        show: false,
                    },
                    axisTicks: {
                        show: false,
                    },
                },
                yaxis: {
                    show: false,
                },
                grid: {
                    show: false,
                },
                dataLabels: {
                    enabled: false,
                },
                tooltip: {
                    enabled: false,
                },
                legend: {
                    show: false,
                },
                states: {
                    active: {
                        filter: {
                            type: 'none',
                        },
                    },
                    hover: {
                        filter: {
                            type: 'none',
                        },
                    },
                },
            },
        };
    },
};
</script>

<style scoped lang="scss">
h2 {
    margin-top: 3rem;
}

h3 {
    font-size: 20px;
    margin-top: 1.5rem;
}

.bar-chart {
    height: 350px;
    max-width: 650px;
}
</style>
